import { defineStore } from "pinia";
import { ref, computed, reactive } from "vue";
import Utilities from "../Helpers/utilities";
import axios from "axios";
import { useUserStore } from "Admin/js/Stores/User";
import HttpStatusCodes from "Admin/js/Constants/http.js";
import { matchesPeopleSearch } from "Admin/js/Components/Custom/Team/Composables/peopleSearch";

export const useWorkstationConnectStore = defineStore(
    "WorkstationConnectStore",
    () => {
        const userStore = useUserStore();

        const wscUsers = ref([]);
        const wscUsersLoading = ref(false);
        const loading = ref(false);
        const searchQuery = ref("");

        const wscFilter = ref({
            is_active: undefined,
            mandate_on: undefined,
            installed: undefined,
            version: [],
            platform_label: [],
        });

        const filterState = reactive({
            is_active: undefined,
            mandate_on: undefined,
            installed: undefined,
            version: [],
            platform_label: [],
        });

        const isFilterActive = computed(() => {
            return (
                wscFilter.value.is_active !== undefined ||
                wscFilter.value.mandate_on !== undefined ||
                wscFilter.value.installed !== undefined ||
                wscFilter.value.version.length > 0 ||
                wscFilter.value.platform_label.length > 0
            );
        });

        const filterdCount = computed(() => {
            let count = 0;
            wscFilter.value.is_active !== undefined && count++;
            wscFilter.value.mandate_on !== undefined && count++;
            wscFilter.value.installed !== undefined && count++;
            count += wscFilter.value.version.length;
            count += wscFilter.value.platform_label.length;
            return count;
        });

        const filteredWscUsers = computed(() => {
            const { associatedUsers, disassociatedUsers } =
                wscUsers.value.reduce(
                    (acc, user) => {
                        let associateFilter = true;
                        let mandateFilter = true;
                        let installedFilter = true;
                        let versionFilter = true;
                        let platformFilter = true;

                        if (wscFilter.value.is_active !== undefined) {
                            associateFilter =
                                user.is_active === wscFilter.value.is_active;
                        }

                        if (wscFilter.value.mandate_on !== undefined) {
                            mandateFilter =
                                user.mandate_on === wscFilter.value.mandate_on;
                        }

                        if (wscFilter.value.installed !== undefined) {
                            installedFilter =
                                user.installed === wscFilter.value.installed;
                        }

                        if (wscFilter.value.version.length > 0) {
                            versionFilter = wscFilter.value.version.includes(
                                user.version,
                            );
                        }

                        if (wscFilter.value.platform_label.length > 0) {
                            platformFilter =
                                wscFilter.value.platform_label.includes(
                                    user.platform_label,
                                );
                        }

                        const hasInstalledFilter =
                            wscFilter.value.installed !== undefined;
                        const hasVersionFilter =
                            wscFilter.value.version.length > 0;

                        let wscFilterResult = true;

                        if (hasInstalledFilter && hasVersionFilter) {
                            wscFilterResult = installedFilter || versionFilter;
                        } else if (hasInstalledFilter) {
                            wscFilterResult = installedFilter;
                        } else if (hasVersionFilter) {
                            wscFilterResult = versionFilter;
                        }

                        if (
                            associateFilter &&
                            mandateFilter &&
                            wscFilterResult &&
                            platformFilter &&
                            matchesPeopleSearch(user, searchQuery.value)
                        ) {
                            if (user.is_active) {
                                acc.associatedUsers.push(user);
                            } else {
                                acc.disassociatedUsers.push(user);
                            }
                        }
                        return acc;
                    },
                    { associatedUsers: [], disassociatedUsers: [] },
                );

            return [...associatedUsers, ...disassociatedUsers];
        });

        const tableColumns = computed(() => {
            const columns = [
                { key: "name", title: "Name", show: true },
                {
                    key: "is_active",
                    title: "Associated",
                    show: true,
                    center_align: true,
                },
                {
                    key: "mandate",
                    title: "WSC Mandate",
                    show: true,
                    center_align: true,
                },
                { key: "version", title: "WSC Version", show: true },
                { key: "os", title: "WSC Platform", show: true },
                {
                    key: "last_activity",
                    title: "Last WSC Activity",
                    show: true,
                },
                {
                    key: "last_shift_in_time_display",
                    title: "Last Shift In",
                    show: true,
                },
            ];

            if (userStore.isMainKaamfuOrganization()) {
                columns.push({
                    key: "enterprise_support_admin",
                    title: "Support Admin",
                    show: true,
                    center_align: true,
                });
            }

            return columns;
        });

        const getInstalledWscUsers = computed(() =>
            wscUsers.value.filter((user) => user.installed),
        );

        const getUninstalledWscUsers = computed(() =>
            wscUsers.value.filter((user) => !user.installed),
        );

        const getMandatedWscUsers = computed(() =>
            wscUsers.value.filter((user) => user.mandate_on),
        );

        const getNonMandatedWscUsers = computed(() =>
            wscUsers.value.filter((user) => !user.mandate_on),
        );

        const fetchWscUsers = async (showLoader = false) => {
            if (showLoader) {
                wscUsersLoading.value = true;
            }
            if (!userStore.profiles?.length) {
                await userStore.fetchUsersProfiles();
            }

            try {
                const res = await axios.get("/workstation-connect-app-users");
                wscUsers.value = (res?.data?.data || []).filter((user) => {
                    return !user.is_agent;
                });
            } catch (error) {
                console.log("Error fetching WSC users:", error);
            } finally {
                if (showLoader) {
                    wscUsersLoading.value = false;
                }
            }
        };

        const updateWscUserMandate = async (userId, mandateValue) => {
            try {
                loading.value = true;
                const response = await axios.patch(
                    `/profiles/${userId}/force-app`,
                    { is_force_app_shift: mandateValue },
                );

                if (response.data.data) {
                    updateWscUserAssociation(
                        userId,
                        "mandate_on",
                        mandateValue,
                    );
                    Utilities().showSuccessSnackbar("Updated successfully.");
                }

                return true;
            } catch (error) {
                console.log("Error updating mandate:", error);

                Utilities().showErrorSnackbar("Failed to update mandate.");

                return false;
            } finally {
                loading.value = false;
            }
        };

        const disassociateWscUser = async (userId, forceDeactivate = false) => {
            try {
                loading.value = true;
                const forceDeactivateQuery = forceDeactivate
                    ? "?force-deactivate=true"
                    : "";
                const response = await axios.delete(
                    `/user-profiles/${userId}${forceDeactivateQuery}`,
                );
                if (response?.status === HttpStatusCodes.OK) {
                    Utilities().showSuccessSnackbar(
                        response?.data?.data ||
                            "User profile deactivated successfully",
                    );
                    updateWscUserAssociation(userId, "is_active", 0);
                    await syncUserProfileAssociationState(userId, false);
                }
            } catch (error) {
                console.log("Error disassociating user:", error);
                if (error.response?.status === HttpStatusCodes.BAD_REQUEST) {
                    Utilities().showErrorSnackbar(
                        error.response?.data?.message ||
                            "Failed to disassociate user. Please try again.",
                    );
                }
            } finally {
                loading.value = false;
            }
        };

        const associateWscUser = async (userId) => {
            try {
                loading.value = true;
                const response = await axios.post(
                    `/user-profiles/${userId}/reactivate`,
                );
                if (response?.status === 200) {
                    Utilities().showSuccessSnackbar(
                        response?.data?.data ||
                            "User profile reactivated successfully",
                    );
                    updateWscUserAssociation(userId, "is_active", 1);
                    await syncUserProfileAssociationState(userId, true);
                }
            } catch (error) {
                console.log("Error associating user:", error);
                Utilities().showErrorSnackbar("Failed to associate user.");
            } finally {
                loading.value = false;
            }
        };

        const updateWscUserEnterpriseSupportAdmin = async (
            userId,
            isEnterpriseSupportAdmin,
        ) => {
            try {
                loading.value = true;
                const response = await axios.post(
                    `/kaamfu-enterprise-support-admin`,
                    {
                        user_profile_id: userId,
                        is_support_admin: isEnterpriseSupportAdmin,
                    },
                );
                if (response?.status === 200) {
                    Utilities().showSuccessSnackbar("Updated successfully.");
                    updateWscUserAssociation(
                        userId,
                        "is_support_admin",
                        isEnterpriseSupportAdmin,
                    );
                }
            } catch (error) {
                console.log("Error updating enterprise support admin:", error);

                const errorMessage =
                    error.response?.data?.errors?.[
                        Object.keys(error.response.data.errors || {})[0]
                    ]?.[0] ||
                    error.response?.data?.message ||
                    "Failed to update enterprise support admin.";

                Utilities().showErrorSnackbar(errorMessage);
            } finally {
                loading.value = false;
            }
        };

        const fetchESAOrgCount = async (userId) => {
            try {
                loading.value = true;
                const response = await axios.get(
                    `/kaamfu-enterprise-support-admin/active/${userId}/count`,
                );
                return response.data.data;
            } catch (error) {
                console.log("Error fetching ESA org count:", error);
                return 0;
            } finally {
                loading.value = false;
            }
        };

        const updateWscUserAssociation = (userId, field, data) => {
            const userIndex = wscUsers.value.findIndex(
                (user) => user.id === userId,
            );
            if (userIndex !== -1) {
                wscUsers.value[userIndex] = {
                    ...wscUsers.value[userIndex],
                    [field]: data,
                };
            }
        };

        const syncUserProfileAssociationState = async (userId, isActive) => {
            const profile = userStore.getUserProfile(userId);

            if (!profile) {
                return;
            }

            await userStore.replaceUserProfile({
                id: userId,
                is_active: isActive ? 1 : 0,
            });
        };

        const resetWscUsers = () => {
            wscUsers.value = [];
            wscUsersLoading.value = false;
            searchQuery.value = "";
        };

        return {
            wscUsers,
            wscUsersLoading,
            loading,
            searchQuery,
            wscFilter,
            filterState,
            tableColumns,

            getInstalledWscUsers,
            getUninstalledWscUsers,
            getMandatedWscUsers,
            getNonMandatedWscUsers,
            filteredWscUsers,
            isFilterActive,
            filterdCount,

            fetchWscUsers,
            updateWscUserMandate,
            updateWscUserAssociation,
            updateWscUserEnterpriseSupportAdmin,
            disassociateWscUser,
            associateWscUser,
            fetchESAOrgCount,
            resetWscUsers,
        };
    },
);
